import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { AddCircleOutline, DeleteOutline } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { tokens } from './v4Styles'

const APPLICATION_AREAS = ['Ornamental/Right-of-Way', 'Turf', 'Bare Ground']
const SEASON_OPTIONS = ['Warm', 'Cold', 'All']
const TYPE_OPTIONS = ['Pre Emergent', 'Post', 'Combo', 'All']
const RATE_UNITS = ['oz', 'gal']
const RATE_PERS = ['gal', '100gal', 'acre', '1000sqft']

const EMPTY_CHEMICAL = {
  chemicalId: '',
  name: '',
  spotRate: '',
  spotUnit: 'oz',
  spotPer: 'acre',
  broadcastRate: '',
  broadcastUnit: 'oz',
  broadcastPer: 'acre'
}

const EMPTY_FORM = {
  name: '',
  vendorId: '',
  season: 'All',
  type: 'All',
  applicationAreas: [],
  calibrationRate: 20,
  active: true,
  chemicals: [{ ...EMPTY_CHEMICAL }]
}

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    background: '#f6f8fb',
    padding: theme.spacing(2)
  },
  headerCard: {
    border: `1px solid ${tokens.border.light}`,
    boxShadow: 'none',
    borderRadius: 8,
    marginBottom: theme.spacing(2)
  },
  tableCard: {
    border: `1px solid ${tokens.border.light}`,
    boxShadow: 'none',
    borderRadius: 8
  },
  title: {
    fontWeight: 700,
    color: tokens.text.primary
  },
  helper: {
    color: tokens.text.secondary
  },
  tableHead: {
    fontWeight: 700,
    color: tokens.text.secondary,
    background: '#fafafa',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0
  },
  primaryText: {
    color: tokens.text.primary,
    fontWeight: 700
  },
  secondaryText: {
    color: tokens.text.secondary,
    fontSize: 12
  },
  chipWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6
  },
  chemistryBlock: {
    border: `1px solid ${tokens.border.light}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    background: '#fafafa'
  },
  stat: {
    minWidth: 104,
    border: `1px solid ${tokens.border.light}`,
    borderRadius: 8,
    padding: theme.spacing(1),
    background: '#fff'
  }
}))

const buildChemicalLabel = (chemical) => (
  chemical?.name || chemical?.altName || chemical?.description || 'Unnamed chemical'
)

const hydrateFormFromBlend = (blend) => ({
  name: blend?.name || '',
  vendorId: blend?.subcontractorVendor || '',
  season: blend?.season || 'All',
  type: blend?.type || 'All',
  applicationAreas: Array.isArray(blend?.applicationAreas) ? blend.applicationAreas : [],
  calibrationRate: blend?.calibrationRate ?? 20,
  active: blend?.active !== false,
  chemicals: Array.isArray(blend?.chemicals) && blend.chemicals.length
    ? blend.chemicals.map((chemical) => ({
      chemicalId: chemical?.chemicalId || '',
      name: chemical?.name || '',
      spotRate: chemical?.spot?.rate ?? '',
      spotUnit: chemical?.spot?.unit || 'oz',
      spotPer: chemical?.spot?.per || 'acre',
      broadcastRate: chemical?.broadcast?.rate ?? '',
      broadcastUnit: chemical?.broadcast?.unit || 'oz',
      broadcastPer: chemical?.broadcast?.per || 'acre'
    }))
    : [{ ...EMPTY_CHEMICAL }]
})

const sanitizeChemicalRow = (row, chemicals) => {
  const selectedChemical = chemicals.find((chemical) => String(chemical._id) === String(row.chemicalId))
  const spotRate = row.spotRate === '' ? '' : Number(row.spotRate)
  const broadcastRate = row.broadcastRate === '' ? '' : Number(row.broadcastRate)
  const payload = {
    chemicalId: row.chemicalId,
    name: buildChemicalLabel(selectedChemical) || row.name
  }

  if (row.spotRate !== '') {
    payload.spot = {
      rate: Number.isFinite(spotRate) ? spotRate : row.spotRate,
      unit: row.spotUnit,
      per: row.spotPer
    }
  }
  if (row.broadcastRate !== '') {
    payload.broadcast = {
      rate: Number.isFinite(broadcastRate) ? broadcastRate : row.broadcastRate,
      unit: row.broadcastUnit,
      per: row.broadcastPer
    }
  }

  return payload
}

const validateForm = (form, chemicals) => {
  if (!form.name.trim()) return 'Blend name is required.'
  if (!form.vendorId) return 'Select a subcontractor.'
  if (!form.applicationAreas.length) return 'Select at least one application area.'
  if (!form.chemicals.length) return 'Add at least one chemical.'

  for (const row of form.chemicals) {
    if (!row.chemicalId) return 'Each chemical row must select a chemical.'
    if (row.spotRate === '' && row.broadcastRate === '') return 'Each chemical row needs a spot or broadcast rate.'
    if (!chemicals.find((chemical) => String(chemical._id) === String(row.chemicalId))) {
      return 'One selected chemical is no longer available.'
    }
  }

  return ''
}

const formatDate = (value) => {
  if (!value) return 'Not saved yet'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Not saved yet'
  return date.toLocaleDateString()
}

const SubcontractorBlendManager = ({ url }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [items, setItems] = useState([])
  const [vendors, setVendors] = useState([])
  const [chemicals, setChemicals] = useState([])
  const [summary, setSummary] = useState({})
  const [vendorFilter, setVendorFilter] = useState('all')
  const [includeInactive, setIncludeInactive] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingBlend, setEditingBlend] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)

  const endpoint = `${url}/api/herbicide-schedule/admin/subcontractor-blends`

  const loadBlends = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await axios.get(endpoint, {
        params: {
          includeInactive: includeInactive ? 'true' : undefined,
          vendorId: vendorFilter !== 'all' ? vendorFilter : undefined
        }
      })
      setItems(response.data?.items || [])
      setVendors(response.data?.vendors || [])
      setChemicals(response.data?.chemicals || [])
      setSummary(response.data?.summary || {})
    } catch (requestError) {
      console.error('Failed to load subcontractor blends', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load subcontractor blends.')
    } finally {
      setLoading(false)
    }
  }, [endpoint, includeInactive, vendorFilter])

  useEffect(() => {
    loadBlends()
  }, [loadBlends])

  const visibleVendorOptions = useMemo(() => {
    const vendorMap = new Map(vendors.map((vendor) => [String(vendor._id), vendor]))
    items.forEach((blend) => {
      if (blend.vendor?._id && !vendorMap.has(String(blend.vendor._id))) {
        vendorMap.set(String(blend.vendor._id), blend.vendor)
      }
    })
    return Array.from(vendorMap.values()).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  }, [items, vendors])

  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
      chemicals: [{ ...EMPTY_CHEMICAL }]
    })
    setEditingBlend(null)
    setSubmitError('')
  }

  const openCreateDialog = () => {
    resetForm()
    setForm((current) => ({
      ...current,
      vendorId: vendorFilter !== 'all' ? vendorFilter : ''
    }))
    setDialogOpen(true)
  }

  const openEditDialog = (blend) => {
    setEditingBlend(blend)
    setSubmitError('')
    setForm(hydrateFormFromBlend(blend))
    setDialogOpen(true)
  }

  const closeDialog = () => {
    if (saving) return
    setDialogOpen(false)
    resetForm()
  }

  const toggleApplicationArea = (area) => {
    setForm((current) => ({
      ...current,
      applicationAreas: current.applicationAreas.includes(area)
        ? current.applicationAreas.filter((item) => item !== area)
        : [...current.applicationAreas, area]
    }))
  }

  const updateChemicalRow = (index, patch) => {
    setForm((current) => ({
      ...current,
      chemicals: current.chemicals.map((row, rowIndex) => {
        if (rowIndex !== index) return row
        const nextRow = { ...row, ...patch }
        if (patch.chemicalId) {
          const selectedChemical = chemicals.find((chemical) => String(chemical._id) === String(patch.chemicalId))
          nextRow.name = buildChemicalLabel(selectedChemical)
        }
        return nextRow
      })
    }))
  }

  const addChemicalRow = () => {
    setForm((current) => ({
      ...current,
      chemicals: [...current.chemicals, { ...EMPTY_CHEMICAL }]
    }))
  }

  const removeChemicalRow = (index) => {
    setForm((current) => ({
      ...current,
      chemicals: current.chemicals.filter((_, rowIndex) => rowIndex !== index)
    }))
  }

  const handleSubmit = async () => {
    const validationError = validateForm(form, chemicals)
    if (validationError) {
      setSubmitError(validationError)
      return
    }

    const payload = {
      name: form.name.trim(),
      subcontractorVendor: form.vendorId,
      season: form.season,
      type: form.type,
      applicationAreas: form.applicationAreas,
      calibrationRate: Number(form.calibrationRate) || 20,
      active: form.active,
      chemicals: form.chemicals.map((row) => sanitizeChemicalRow(row, chemicals))
    }

    setSaving(true)
    setSubmitError('')
    try {
      if (editingBlend?._id) {
        await axios.put(`${endpoint}/${editingBlend._id}`, payload)
      } else {
        await axios.post(endpoint, payload)
      }
      setDialogOpen(false)
      resetForm()
      await loadBlends()
    } catch (requestError) {
      console.error('Failed to save subcontractor blend', requestError)
      setSubmitError(requestError?.response?.data?.message || 'Unable to save subcontractor blend.')
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivate = async (blend) => {
    const confirmed = window.confirm(`Deactivate ${blend.name}?`)
    if (!confirmed) return

    try {
      await axios.delete(`${endpoint}/${blend._id}`)
      await loadBlends()
    } catch (requestError) {
      console.error('Failed to deactivate subcontractor blend', requestError)
      setError(requestError?.response?.data?.message || 'Unable to deactivate that blend.')
    }
  }

  return (
    <div className={classes.root}>
      <Card className={classes.headerCard}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" style={{ gap: 12 }}>
            <div>
              <Typography variant="h6" className={classes.title}>Subcontractor Chemical Blends</Typography>
              <Typography variant="body2" className={classes.helper}>
                View and modify subcontractor-owned herbicide blends used by the field app.
              </Typography>
            </div>
            <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 10 }}>
              <div className={classes.stat}>
                <Typography variant="caption" className={classes.helper}>Total</Typography>
                <Typography variant="h6" className={classes.title}>{summary.total || items.length || 0}</Typography>
              </div>
              <div className={classes.stat}>
                <Typography variant="caption" className={classes.helper}>Active</Typography>
                <Typography variant="h6" className={classes.title}>{summary.active || items.filter(item => item.active !== false).length}</Typography>
              </div>
              <div className={classes.stat}>
                <Typography variant="caption" className={classes.helper}>Chemicals</Typography>
                <Typography variant="h6" className={classes.title}>{chemicals.length}</Typography>
              </div>
            </Box>
          </Box>
          <Box mt={2} display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 10 }}>
            <TextField
              select
              variant="outlined"
              size="small"
              label="Subcontractor"
              value={vendorFilter}
              onChange={(event) => setVendorFilter(event.target.value)}
              style={{ minWidth: 260 }}
            >
              <MenuItem value="all">All subcontractors</MenuItem>
              {visibleVendorOptions.map((vendor) => (
                <MenuItem key={vendor._id} value={vendor._id}>{vendor.name}</MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeInactive}
                  onChange={(event) => setIncludeInactive(event.target.checked)}
                  color="primary"
                />
              }
              label="Show inactive"
            />
            <Button variant="outlined" onClick={loadBlends} disabled={loading}>
              Refresh
            </Button>
            <Button color="primary" variant="contained" onClick={openCreateDialog}>
              Add Blend
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card className={classes.tableCard}>
        <CardContent>
          {error ? (
            <Box p={3} textAlign="center">
              <Typography color="error">{error}</Typography>
              <Box mt={1}>
                <Button variant="outlined" onClick={loadBlends}>Retry</Button>
              </Box>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHead}>Blend</TableCell>
                    <TableCell className={classes.tableHead}>Subcontractor</TableCell>
                    <TableCell className={classes.tableHead}>Season / Type</TableCell>
                    <TableCell className={classes.tableHead}>Coverage</TableCell>
                    <TableCell className={classes.tableHead}>Chemicals</TableCell>
                    <TableCell className={classes.tableHead}>Updated</TableCell>
                    <TableCell className={classes.tableHead} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && !items.length ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box p={4}><CircularProgress size={28} /></Box>
                      </TableCell>
                    </TableRow>
                  ) : items.length ? items.map((blend) => (
                    <TableRow key={blend._id} hover>
                      <TableCell>
                        <Typography variant="body2" className={classes.primaryText}>{blend.name}</Typography>
                        <Typography variant="body2" className={classes.secondaryText}>
                          {blend.active === false ? 'Inactive' : 'Active'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className={classes.primaryText}>
                          {blend.subcontractorVendorName || blend.vendor?.name || 'Unknown vendor'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className={classes.primaryText}>{blend.season} / {blend.type}</Typography>
                        <Typography variant="body2" className={classes.secondaryText}>Calibration {blend.calibrationRate || 20} gal/acre</Typography>
                      </TableCell>
                      <TableCell>
                        <div className={classes.chipWrap}>
                          {(blend.applicationAreas || []).map((area) => (
                            <Chip key={area} label={area} size="small" />
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className={classes.primaryText}>
                          {(blend.chemicals || []).length} chemical{(blend.chemicals || []).length === 1 ? '' : 's'}
                        </Typography>
                        <Typography variant="body2" className={classes.secondaryText}>
                          {(blend.chemicals || []).slice(0, 2).map((chemical) => chemical.name).join(', ') || 'No chemical names'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(blend.updatedAt)}</TableCell>
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                          <Button size="small" variant="outlined" onClick={() => openEditDialog(blend)}>Edit</Button>
                          {blend.active !== false ? (
                            <Button size="small" onClick={() => handleDeactivate(blend)} style={{ color: '#c62828' }}>
                              Deactivate
                            </Button>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box p={4}>
                          <Typography variant="body2" className={classes.helper}>No subcontractor blends found.</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingBlend ? 'Edit Subcontractor Blend' : 'Add Subcontractor Blend'}</DialogTitle>
        <DialogContent dividers>
          {submitError ? (
            <Box mb={2}>
              <Typography color="error">{submitError}</Typography>
            </Box>
          ) : null}
          <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" style={{ gap: 16 }}>
            <TextField
              label="Blend Name"
              variant="outlined"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              fullWidth
            />
            <TextField
              select
              label="Subcontractor"
              variant="outlined"
              value={form.vendorId}
              onChange={(event) => setForm({ ...form, vendorId: event.target.value })}
              fullWidth
            >
              {visibleVendorOptions.map((vendor) => (
                <MenuItem key={vendor._id} value={vendor._id}>{vendor.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Season"
              variant="outlined"
              value={form.season}
              onChange={(event) => setForm({ ...form, season: event.target.value })}
              fullWidth
            >
              {SEASON_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Type"
              variant="outlined"
              value={form.type}
              onChange={(event) => setForm({ ...form, type: event.target.value })}
              fullWidth
            >
              {TYPE_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </TextField>
            <TextField
              label="Calibration Rate"
              variant="outlined"
              type="number"
              value={form.calibrationRate}
              onChange={(event) => setForm({ ...form, calibrationRate: event.target.value })}
              fullWidth
              helperText="Gallons per acre"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.active}
                  onChange={(event) => setForm({ ...form, active: event.target.checked })}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" className={classes.title}>Application Areas</Typography>
            <Box display="flex" flexWrap="wrap" mt={1} style={{ gap: 10 }}>
              {APPLICATION_AREAS.map((area) => (
                <FormControlLabel
                  key={area}
                  control={
                    <Checkbox
                      checked={form.applicationAreas.includes(area)}
                      onChange={() => toggleApplicationArea(area)}
                      color="primary"
                    />
                  }
                  label={area}
                />
              ))}
            </Box>
          </Box>

          <Box mt={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2" className={classes.title}>Chemicals</Typography>
              <Button size="small" startIcon={<AddCircleOutline />} onClick={addChemicalRow}>Add Chemical</Button>
            </Box>
            {form.chemicals.map((row, index) => (
              <div key={`${row.chemicalId}-${index}`} className={classes.chemistryBlock}>
                <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                  <TextField
                    select
                    label="Chemical"
                    variant="outlined"
                    size="small"
                    value={row.chemicalId}
                    onChange={(event) => updateChemicalRow(index, { chemicalId: event.target.value })}
                    fullWidth
                  >
                    {chemicals.map((chemical) => (
                      <MenuItem key={chemical._id} value={chemical._id}>{buildChemicalLabel(chemical)}</MenuItem>
                    ))}
                  </TextField>
                  <IconButton
                    size="small"
                    onClick={() => removeChemicalRow(index)}
                    disabled={form.chemicals.length === 1}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Box>
                <Box mt={1.5} display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" style={{ gap: 10 }}>
                  <TextField
                    label="Spot Rate"
                    variant="outlined"
                    size="small"
                    type="number"
                    value={row.spotRate}
                    onChange={(event) => updateChemicalRow(index, { spotRate: event.target.value })}
                  />
                  <TextField
                    select
                    label="Spot Unit"
                    variant="outlined"
                    size="small"
                    value={row.spotUnit}
                    onChange={(event) => updateChemicalRow(index, { spotUnit: event.target.value })}
                  >
                    {RATE_UNITS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                  </TextField>
                  <TextField
                    select
                    label="Spot Per"
                    variant="outlined"
                    size="small"
                    value={row.spotPer}
                    onChange={(event) => updateChemicalRow(index, { spotPer: event.target.value })}
                  >
                    {RATE_PERS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                  </TextField>
                </Box>
                <Box mt={1.5} display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" style={{ gap: 10 }}>
                  <TextField
                    label="Broadcast Rate"
                    variant="outlined"
                    size="small"
                    type="number"
                    value={row.broadcastRate}
                    onChange={(event) => updateChemicalRow(index, { broadcastRate: event.target.value })}
                  />
                  <TextField
                    select
                    label="Broadcast Unit"
                    variant="outlined"
                    size="small"
                    value={row.broadcastUnit}
                    onChange={(event) => updateChemicalRow(index, { broadcastUnit: event.target.value })}
                  >
                    {RATE_UNITS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                  </TextField>
                  <TextField
                    select
                    label="Broadcast Per"
                    variant="outlined"
                    size="small"
                    value={row.broadcastPer}
                    onChange={(event) => updateChemicalRow(index, { broadcastPer: event.target.value })}
                  >
                    {RATE_PERS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                  </TextField>
                </Box>
              </div>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Blend'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default SubcontractorBlendManager
